
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Home, AlertCircle } from 'lucide-react';
import Header from '@/components/Header';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResendButton, setShowResendButton] = useState(false);
  const { signIn, resendConfirmation } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/account';

  // Check for redirect message and show toast
  useEffect(() => {
    const redirectMessage = sessionStorage.getItem('redirectMessage');
    if (redirectMessage) {
      try {
        const message = JSON.parse(redirectMessage);
        toast.error(message.title, {
          description: message.description,
          duration: 6000
        });
        // Clear the message so it doesn't show again
        sessionStorage.removeItem('redirectMessage');
      } catch (error) {
        console.error('Error parsing redirect message:', error);
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Client-side validation
    if (!email) {
      setError(t('email_required', 'البريد الإلكتروني مطلوب', 'Email is required'));
      setLoading(false);
      return;
    }

    if (!password) {
      setError(t('password_required', 'كلمة المرور مطلوبة', 'Password is required'));
      setLoading(false);
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('invalid_email_format', 'تنسيق البريد الإلكتروني غير صحيح', 'Please enter a valid email address'));
      setLoading(false);
      return;
    }

    try {
      const { error } = await signIn(email, password);
      
      if (error) {
        const errorMessage = error.message.toLowerCase();
        const errorCode = error.message;
        
        // Handle email not confirmed
        if (errorMessage.includes('email not confirmed') || 
            errorMessage.includes('email_not_confirmed') ||
            errorMessage.includes('confirm your email') ||
            errorCode.includes('Email not confirmed')) {
          setShowResendButton(true);
          setError(t('account_not_verified', 'حسابك غير مُفعل. تحقق من بريدك الإلكتروني لتفعيل الحساب', 'Your account is not verified. Please check your email for the verification link to activate your account.'));
          toast.error(t('email_verification_required', 'يجب تفعيل البريد الإلكتروني', 'Email verification required. Please check your inbox.'));
        } 
        // Handle invalid credentials (wrong password or email)
        else if (errorMessage.includes('invalid login credentials') || 
                 errorMessage.includes('invalid credentials') ||
                 errorMessage.includes('invalid email or password')) {
          setError(t('invalid_credentials', 'البريد الإلكتروني أو كلمة المرور غير صحيحة', 'The email address or password you entered is incorrect. Please double-check your credentials.'));
          setShowResendButton(false);
          toast.error(t('login_failed', 'فشل تسجيل الدخول', 'Login failed. Please check your email and password.'));
        }
        // Handle user not found
        else if (errorMessage.includes('user not found') || errorMessage.includes('no user found')) {
          setError(t('user_not_found', 'لم يتم العثور على هذا الحساب', 'No account found with this email address. Please sign up first.'));
          setShowResendButton(false);
          toast.error(t('account_not_found', 'الحساب غير موجود', 'Account not found. Please check your email or sign up.'));
        }
        // Handle other errors
        else {
          setError(error.message);
          setShowResendButton(false);
          toast.error(t('sign_in_error', 'خطأ في تسجيل الدخول', 'Sign-in error. Please try again.'));
        }
      } else {
        toast.success(t('sign_in_success', 'تم تسجيل الدخول بنجاح', 'Signed in successfully'));
        navigate(from, { replace: true });
      }
    } catch (error: any) {
      console.error('Sign in error:', error);
      setError(t('sign_in_error', 'خطأ في تسجيل الدخول', 'Error signing in'));
      setShowResendButton(false);
      toast.error(t('unexpected_error', 'حدث خطأ غير متوقع', 'An unexpected error occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error(t('enter_email_first', 'أدخل بريدك الإلكتروني أولاً', 'Please enter your email address first'));
      return;
    }
    
    try {
      await resendConfirmation(email);
      setShowResendButton(false);
      toast.success(t('verification_resent', 'تم إرسال رابط التحقق مرة أخرى', 'Verification email sent! Please check your inbox.'));
    } catch (error) {
      toast.error(t('resend_failed', 'فشل في إعادة الإرسال', 'Failed to resend verification email'));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className={`flex items-center justify-center py-12 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        <div className="w-full max-w-md space-y-8 p-6">
          <div className="text-center">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <Home className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">{t('maskan', 'مسكن', 'Maskan')}</span>
            </Link>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('sign_in', 'تسجيل الدخول', 'Sign In')}</CardTitle>
              <CardDescription>
                {t('sign_in_description', 'أدخل بياناتك لتسجيل الدخول', 'Enter your credentials to sign in')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                      <AlertCircle className="h-4 w-4" />
                      <span>{error}</span>
                    </div>
                    {showResendButton && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={handleResendVerification}
                        className="w-full"
                      >
                        {t('resend_verification', 'إعادة إرسال رابط التحقق', 'Resend Verification Email')}
                      </Button>
                    )}
                  </div>
                )}
                
                <div>
                  <Label htmlFor="email">{t('email', 'البريد الإلكتروني', 'Email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={error && !email ? 'border-red-300' : ''}
                  />
                </div>
                <div>
                  <Label htmlFor="password">{t('password', 'كلمة المرور', 'Password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={error && !password ? 'border-red-300' : ''}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? t('signing_in', 'جاري تسجيل الدخول...', 'Signing in...') : t('sign_in', 'تسجيل الدخول', 'Sign In')}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('no_account', 'ليس لديك حساب؟', "Don't have an account?")}{' '}
                  <Link to="/signup" className="text-primary hover:underline">
                    {t('sign_up', 'إنشاء حساب', 'Sign up')}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
